declare global {
  interface Window {
    google?: {
      maps: GoogleMapsNamespace & {
        importLibrary?: (name: string) => Promise<unknown>;
        places?: Record<string, unknown>;
      };
    };
  }
}

import { buildGoogleMapsJsUrl } from './googleMapsBaseUrl';

// Minimal typings for dynamic SDK objects
export interface GoogleMapsNamespace {
  Map: new (el: HTMLElement, opts: Record<string, unknown>) => GoogleMapInstance;
  InfoWindow: new (opts?: Record<string, unknown>) => GoogleInfoWindowInstance;
  Marker: new (opts: Record<string, unknown>) => GoogleMarkerInstance;
  Polyline: new (opts: Record<string, unknown>) => GooglePolylineInstance;
  LatLng: new (lat: number, lng: number) => unknown;
  LatLngBounds: new () => GoogleBoundsInstance;
  Size: new (width: number, height: number) => GoogleSizeInstance;
  Point: new (x: number, y: number) => GooglePointInstance;
  Geocoder: new () => GoogleGeocoderInstance;
  DirectionsService: new () => GoogleDirectionsService;
  DirectionsRenderer: new (opts: Record<string, unknown>) => GoogleDirectionsRenderer;
  DistanceMatrixService: new () => GoogleDistanceMatrixService;
  TravelMode: { DRIVING: string };
  TrafficModel: { BEST_GUESS: string };
  UnitSystem: { METRIC: number };
  SymbolPath: { CIRCLE: unknown };
}

export interface GoogleSizeInstance {
  width: number;
  height: number;
}

export interface GooglePointInstance {
  x: number;
  y: number;
}

export interface GoogleMapInstance {
  panTo: (pos: { lat: number; lng: number }) => void;
  fitBounds: (bounds: GoogleBoundsInstance) => void;
  setZoom?: (zoom: number) => void;
  setCenter?: (pos: { lat: number; lng: number }) => void;
}

export interface GoogleMarkerInstance {
  setMap: (map: GoogleMapInstance | null) => void;
  setPosition: (pos: { lat: number; lng: number }) => void;
  setLabel?: (label: { text: string; color?: string; fontSize?: string }) => void;
  addListener?: (event: string, handler: () => void) => void;
}

export interface GoogleInfoWindowInstance {
  setContent: (content: string) => void;
  open: (map: GoogleMapInstance | null, anchor?: GoogleMarkerInstance) => void;
  close: () => void;
}

export interface GooglePolylineInstance {
  setMap: (map: GoogleMapInstance | null) => void;
  setPath: (path: { lat: number; lng: number }[]) => void;
}

export interface GoogleBoundsInstance {
  extend: (pos: { lat: number; lng: number }) => void;
}

export interface GoogleGeocoderInstance {
  geocode: (
    req: { location?: { lat: number; lng: number }; address?: string; componentRestrictions?: { country: string } },
    cb: (
      results: {
        formatted_address: string;
        place_id?: string;
        geometry?: { location?: { lat: () => number; lng: () => number } };
      }[] | null,
      status: string
    ) => void
  ) => void;
}

export interface GoogleDirectionsService {
  route: (
    req: Record<string, unknown>,
    cb: (result: unknown, status: string) => void
  ) => void;
}

export interface GoogleDirectionsRenderer {
  setMap: (map: GoogleMapInstance | null) => void;
  setDirections: (directions: unknown) => void;
  getMap?: () => GoogleMapInstance | null;
}

export interface GoogleDistanceMatrixService {
  getDistanceMatrix: (
    req: Record<string, unknown>,
    cb: (response: GoogleDistanceMatrixResponse | null, status: string) => void
  ) => void;
}

export interface GoogleDistanceMatrixResponse {
  rows?: {
    elements?: {
      status: string;
      distance?: { value: number };
      duration?: { value: number };
      duration_in_traffic?: { value: number };
    }[];
  }[];
}

let googleLoadPromise: Promise<GoogleMapsNamespace> | null = null;

export function loadGoogleMaps(apiKey: string): Promise<GoogleMapsNamespace> {
  if (!apiKey) {
    return Promise.reject(new Error('Google Maps API key is not configured'));
  }
  if (window.google?.maps) {
    return Promise.resolve(window.google.maps);
  }
  if (googleLoadPromise) return googleLoadPromise;

  googleLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = buildGoogleMapsJsUrl(apiKey);
    script.async = true;
    const timeoutMs = 20000;
    const timeoutId = window.setTimeout(() => {
      reject(new Error('Google Maps script load timed out — check network or proxy settings'));
    }, timeoutMs);
    script.onload = () => {
      window.clearTimeout(timeoutId);
      if (window.google?.maps) resolve(window.google.maps);
      else reject(new Error('Google Maps failed to initialize'));
    };
    script.onerror = () => {
      window.clearTimeout(timeoutId);
      reject(new Error('Failed to load Google Maps script'));
    };
    document.head.appendChild(script);
  });

  return googleLoadPromise;
}
